import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { IUserShort } from 'common/models';
import { AppDispatch, RootState } from 'app/store';
import {
  IInventoryAction,
  IInventoryActionCreatePayload,
  IInventoryActionDeletePayload,
  IInventoryActionListPayload,
  IInventoryActionListUsersParams,
  IInventoryActionUpdatePayload,
} from 'entities/InventoryAction/InventoryAction.models';
import { inventoryActionTransport } from 'entities/InventoryAction/InventoryAction.transport';
import { addInventoryListItemAction } from 'entities/Inventory/Inventory.slice';

interface IState {
  inventoryAction: IInventoryAction | null;
  inventoryActionList: IInventoryAction[];
  inventoryActionCount: number;
  isInventoryActionLoading: boolean;
  inventoryActionListUsers: IUserShort[];
}

const initialState: IState = {
  inventoryAction: null,
  inventoryActionList: [],
  inventoryActionCount: 0,
  isInventoryActionLoading: false,
  inventoryActionListUsers: [],
};

export const inventoryActionSlice = createSlice({
  name: 'inventoryAction',
  initialState,
  reducers: {
    setInventoryAction(state, action: PayloadAction<IInventoryAction | null>) {
      state.inventoryAction = action.payload;
    },
    setInventoryActionList(state, action: PayloadAction<IInventoryAction[]>) {
      state.inventoryActionList = action.payload;
    },
    setInventoryActionListChunk(state, action: PayloadAction<IInventoryAction[]>) {
      state.inventoryActionList.push(...action.payload);
    },
    setInventoryActionCount(state, action: PayloadAction<number>) {
      state.inventoryActionCount = action.payload;
    },
    setIsInventoryActionLoading(state, action: PayloadAction<boolean>) {
      state.isInventoryActionLoading = action.payload;
    },
    setInventoryActionListUsers(state, action: PayloadAction<IUserShort[]>) {
      state.inventoryActionListUsers = action.payload;
    },
    filterInventoryActionList(state, action: PayloadAction<IInventoryAction[]>) {
      const inventoryActionIds = action.payload.map((item) => item.id);

      state.inventoryActionList = state.inventoryActionList.filter((item) => !inventoryActionIds.includes(item.id));
      state.inventoryActionCount = state.inventoryActionCount - action.payload.length;
    },
  },
});

export const {
  setInventoryAction,
  setInventoryActionList,
  setInventoryActionListChunk,
  setInventoryActionCount,
  setIsInventoryActionLoading,
  setInventoryActionListUsers,
  filterInventoryActionList,
} = inventoryActionSlice.actions;

export const selectInventoryAction = (state: RootState) => state.inventoryAction.inventoryAction;
export const selectInventoryActionList = (state: RootState) => state.inventoryAction.inventoryActionList;
export const selectInventoryActionCount = (state: RootState) => state.inventoryAction.inventoryActionCount;
export const selectIsInventoryActionLoading = (state: RootState) => state.inventoryAction.isInventoryActionLoading;
export const selectInventoryActionListUsers = (state: RootState) => state.inventoryAction.inventoryActionListUsers;

export const inventoryActionReducer = inventoryActionSlice.reducer;

export const getInventoryActionList = (payload: IInventoryActionListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryActionLoading(true));

  try {
    const response = await inventoryActionTransport.get(payload);
    dispatch(setInventoryActionList(response.data));
    dispatch(setInventoryActionCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryActionLoading(false));
  }
};

export const getInventoryActionListBatch = (payload: IInventoryActionListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryActionLoading(true));

  try {
    const response = await inventoryActionTransport.get(payload);
    dispatch(setInventoryActionListChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryActionLoading(false));
  }
};

export const getInventoryActionListUsers = (params: IInventoryActionListUsersParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryActionLoading(true));

  try {
    const response = await inventoryActionTransport.getUsers(params);
    dispatch(setInventoryActionListUsers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryActionLoading(false));
  }
};

export const getInventoryActionById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryActionLoading(true));

  try {
    const response = await inventoryActionTransport.getById(id);
    dispatch(setInventoryAction(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryActionLoading(false));
  }
};

export const createInventoryAction = (payload: IInventoryActionCreatePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryActionLoading(true));

  try {
    const response = await inventoryActionTransport.create(payload);
    dispatch(addInventoryListItemAction(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryActionLoading(false));
  }
};

export const updateInventoryAction = (payload: IInventoryActionUpdatePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryActionLoading(true));

  try {
    await inventoryActionTransport.update(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryActionLoading(false));
  }
};

export const deleteInventoryAction = (payload: IInventoryActionDeletePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryActionLoading(true));

  try {
    await inventoryActionTransport.delete(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryActionLoading(false));
  }
};
